// components/AddCardGuestsModal.js - Select guests for private card access
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiCheck, FiUser } from 'react-icons/fi';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import '../styles/AddCardGuestsModal.scss';

const AddCardGuestsModal = ({ visible, onClose, selectedUserIds, onUsersSelected }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const loadUsers = useCallback(async () => {
    if (!user) return; // signed out: nobody to look up (user.id would throw)
    try {
      setLoading(true);

      // People you have actually shared something with.
      //
      // This read a `followers` table, which does not exist in this database
      // — the request 404d, the catch swallowed it, and the picker showed an
      // empty list with "Error loading users" in the console. There is no
      // follow graph in this product; what there is is a history of events
      // you have been at together, which is a better answer anyway: the
      // people you would send a card to are the people you have celebrated
      // with.
      //
      // Two hops: the events this user is going to, then everyone else going
      // to those events.
      const { data: myRsvps, error: rsvpError } = await supabase
        .from('event_rsvps')
        .select('event_id')
        .eq('user_id', user.id)
        .eq('status', 'going')
        .limit(100);

      if (rsvpError) throw rsvpError;

      const eventIds = (myRsvps ?? []).map((r) => r.event_id);

      const userIds = new Set();

      if (eventIds.length > 0) {
        const { data: others, error: othersError } = await supabase
          .from('event_rsvps')
          .select('user_id')
          .in('event_id', eventIds)
          .eq('status', 'going')
          .limit(500);

        if (othersError) throw othersError;
        others?.forEach((r) => {
          if (r.user_id && r.user_id !== user.id) userIds.add(r.user_id);
        });
      }

      // Plus anyone hosting something this user is going to.
      if (eventIds.length > 0) {
        const { data: hosts } = await supabase
          .from('events')
          .select('host_id')
          .in('id', eventIds);
        hosts?.forEach((e) => {
          if (e.host_id && e.host_id !== user.id) userIds.add(e.host_id);
        });
      }

      if (userIds.size === 0) {
        setUsers([]);
        return;
      }

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, profile_photo_cdn_url, avatar_config')
        .in('id', Array.from(userIds))
        .order('first_name', { ascending: true });

      if (profilesError) throw profilesError;

      setUsers(profiles || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (visible) {
      setSelectedIds(selectedUserIds || []);
      loadUsers();
    }
  }, [visible, selectedUserIds, loadUsers]);

  const handleToggleUser = (userId) => {
    setSelectedIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSave = () => {
    onUsersSelected(selectedIds);
    onClose();
  };

  const filteredUsers = users.filter(u => {
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const username = u.username?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || username.includes(query);
  });

  const getUserName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="add-card-guests-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header">
            <h3>Add People</h3>
            <button className="btn-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          {/* Search */}
          <div className="search-section">
            <div className="search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by name or username"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Selected Count */}
          {selectedIds.length > 0 && (
            <div className="selected-count">
              {selectedIds.length} {selectedIds.length === 1 ? 'person' : 'people'} selected
            </div>
          )}

          {/* Users List */}
          <div className="users-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading connections...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="empty-state">
                {searchQuery ? (
                  <>
                    <FiSearch className="empty-icon" />
                    <p>No users found matching "{searchQuery}"</p>
                  </>
                ) : (
                  <>
                    <FiUser className="empty-icon" />
                    <p>No connections found</p>
                    <small>Follow users to add them to your private cards</small>
                  </>
                )}
              </div>
            ) : (
              filteredUsers.map(u => (
                <button
                  key={u.id}
                  className={`user-item ${selectedIds.includes(u.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleUser(u.id)}
                >
                  <div className="user-info">
                    {u.profile_photo_cdn_url ? (
                      <img src={u.profile_photo_cdn_url} alt={getUserName(u)} className="user-avatar" />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {getUserName(u).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="user-details">
                      <div className="user-name">{getUserName(u)}</div>
                      {u.username && <div className="user-username">@{u.username}</div>}
                    </div>
                  </div>
                  <div className={`checkbox ${selectedIds.includes(u.id) ? 'checked' : ''}`}>
                    {selectedIds.includes(u.id) && <FiCheck />}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddCardGuestsModal;
